import { Link } from 'react-router-dom';
import { Badge, ProgressBar, Avatar } from '../../components/primitives';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  useVadyStats,
  useDenik,
  useRfi,
  useKontrolniDny,
  usePhotos,
  useHarmonogramy,
  useDocuments,
  useZmenoveListyStats,
} from '../../api/hooks';
import { navSections } from '../../components/layout/navigation';
import styles from './DashboardPage.module.css';
import { cx } from '../../lib/cx';

type ModuleStatus = 'done' | 'pending' | 'draft' | 'empty' | 'future' | 'info';

interface ModuleCardProps {
  to: string;
  cat: string;
  name: string;
  meta: string;
  status?: ModuleStatus;
  count?: string;
  highlight?: boolean;
}

function formatCzDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ');
}

export function DashboardPage() {
  const { activeStavba, stavby, isLoading } = useActiveStavba();
  const sid = activeStavba?.id ?? null;

  const { data: vadyStats } = useVadyStats(sid);
  const { data: denikList = [] } = useDenik(sid);
  const { data: rfiList = [] } = useRfi(sid);
  const { data: kdList = [] } = useKontrolniDny(sid);
  const { data: photos = [] } = usePhotos(sid);
  const { data: harmonogramy = [] } = useHarmonogramy(sid);
  const { data: documents = [] } = useDocuments(
    activeStavba?.nazev ? { stavba: activeStavba.nazev } : undefined,
  );
  const { data: zmenyStats = [] } = useZmenoveListyStats(sid);

  if (isLoading) {
    return <div className={styles.page}><div style={{ color: 'var(--sd-text-dim)' }}>Načítám…</div></div>;
  }

  if (stavby.length === 0) {
    return (
      <div className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.eyebrow}>Vítejte</div>
          <h1 className={styles.titleRow}>Zatím žádná stavba</h1>
          <div className={styles.subtitle}>
            Vytvořte první stavbu v sekci <b>Moje stavby</b>, aby se rozcestník naplnil.
          </div>
        </section>
      </div>
    );
  }

  // Statické popisky pro moduly bez živých statistik.
  const staticMeta: Record<string, string> = {
    '/app/ukoly': 'Delegování a termíny',
    '/app/soupis': 'Čerpání a fakturace',
    '/app/zjistovaci': 'Podklad k fakturaci',
    '/app/objednavka': 'Objednávky prací',
    '/app/reklamace': 'Reklamace vad',
    '/app/kontrolni-dny/nasdilene': 'Sdílené kontrolní dny',
    '/app/bozp/telefony': 'Důležité kontakty',
    '/app/bozp/trauma': 'Postup při úrazu',
    '/app/bozp/seznameni': 'Docházka a školení',
    '/app/bozp/urazy': 'Evidence úrazů',
    '/app/bozp/rizika': 'Dokumentace BOZP',
    '/app/bozp/pozarni-dohled': 'Svařování a otevřený oheň',
    '/app/bozp/karty-zamestnancu': 'Majetek a OOPP',
    '/app/subdodavatele': 'Dodavatelé a kontakty',
    '/app/vyuctovani': 'Útraty a převody',
    '/app/kniha-jizd': 'Evidence jízd',
    '/app/dokumenty-projektu': 'Výkresy a PD',
    '/app/archiv': 'Uložené dokumenty',
  };

  // Rozcestník = úplný seznam modulů z navigace (zdroj pravdy je menu).
  // Rozcestník samotný a přepínač staveb vynecháme.
  const skip = new Set(['/app', '/app/stavby']);

  // „Pracovat mimo stavbu" — žádná aktivní stavba (vědomá volba účetní apod.).
  // Rozcestník bez statistik konkrétní stavby; moduly nezávislé na stavbě
  // (Úkoly, Vyúčtování, BOZP, Kniha jízd…) jdou používat, moduly vázané na
  // stavbu si při otevření vyžádají výběr přes StavbaGate.
  if (!activeStavba) {
    const modules: ModuleCardProps[] = navSections.flatMap((section) =>
      section.items
        .filter((item) => !skip.has(item.to))
        .map((item) => ({
          to: item.to,
          cat: section.label,
          name: item.label,
          meta: staticMeta[item.to] ?? 'Vyžaduje výběr stavby',
          status: (staticMeta[item.to] ? 'info' : 'empty') as ModuleStatus,
        })),
    );

    return (
      <div className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.eyebrow}>Rozcestník</div>
          <h1 className={styles.titleRow}>Pracujete mimo stavbu</h1>
          <div className={styles.subtitle}>
            Žádná stavba není aktivní. Vyberte stavbu v přepínači staveb (na telefonu v menu Více), pokud chcete přehled a
            moduly vázané na konkrétní zakázku. Moduly nezávislé na stavbě (úkoly, vyúčtování,
            BOZP, kniha jízd…) můžete používat i takto.
          </div>
        </section>

        <section>
          <div className={styles.sectionLabel}>Moduly</div>
          <div className={styles.modulesGrid}>
            {modules.map((m) => (
              <ModuleCard key={m.name} {...m} />
            ))}
          </div>
        </section>
      </div>
    );
  }

  const zahajeni = activeStavba.datum_zahajeni || '—';
  const subtitleParts = [activeStavba.adresa, zahajeni && `Zahájení ${zahajeni}`].filter(Boolean);

  const todayIso = new Date().toISOString().slice(0, 10);
  const lastDenikIso = denikList[0]?.datum;
  const dnesDenik = lastDenikIso ? lastDenikIso.slice(0, 10) === todayIso : false;
  const openVady = (vadyStats?.otevreno ?? 0) + (vadyStats?.v_reseni ?? 0);
  const openRfi = rfiList.filter((r) => r.stav !== 'uzavreno').length;
  const schvalenoZmen = zmenyStats.find((s) => s.stav === 'schvaleno')?.count ?? 0;
  const navrhZmen = zmenyStats.find((s) => s.stav === 'navrh')?.count ?? 0;
  const fakturyDocs = documents.filter((d) => d.doc_type === 'faktura');
  const nabidkaDocs = documents.filter((d) => d.doc_type === 'nabidka');
  const predavaciDocs = documents.filter((d) => d.doc_type === 'predavaci');
  const predavaciDiloDocs = documents.filter((d) => d.doc_type === 'predavaci-dilo');
  const predavaciDiloVelkyDocs = documents.filter((d) => d.doc_type === 'predavaci-dilo-velky');
  const lastDenikLabel = lastDenikIso ? `Poslední zápis ${formatCzDate(lastDenikIso)}` : 'Zatím žádný zápis';

  // Moduly, které mají živá data (počty, stavy) ze stavby.
  const liveData: Record<string, Partial<ModuleCardProps>> = {
    '/app/predavaci': {
      meta: predavaciDocs.length > 0 ? `${predavaciDocs.length} uložených` : 'Zatím žádný',
      status: predavaciDocs.length > 0 ? 'done' : 'empty',
      count: predavaciDocs.length > 0 ? String(predavaciDocs.length) : undefined,
    },
    '/app/predavaci-dilo': {
      meta: predavaciDiloDocs.length > 0 ? `${predavaciDiloDocs.length} uložených` : 'Zatím žádný',
      status: predavaciDiloDocs.length > 0 ? 'done' : 'empty',
      count: predavaciDiloDocs.length > 0 ? String(predavaciDiloDocs.length) : undefined,
    },
    '/app/predavaci-dilo-velky': {
      meta: predavaciDiloVelkyDocs.length > 0 ? `${predavaciDiloVelkyDocs.length} uložených` : 'Zatím žádný',
      status: predavaciDiloVelkyDocs.length > 0 ? 'done' : 'empty',
      count: predavaciDiloVelkyDocs.length > 0 ? String(predavaciDiloVelkyDocs.length) : undefined,
    },
    '/app/denik': {
      meta: dnesDenik ? `Zápis dnes ${formatCzDate(todayIso)}` : lastDenikLabel,
      status: dnesDenik ? 'done' : 'pending',
      highlight: !dnesDenik && denikList.length > 0,
      count: String(denikList.length),
    },
    '/app/kontrolni-dny': {
      meta: kdList.length > 0 ? `${kdList.length} evidovaných` : 'Zatím žádné',
      count: kdList.length > 0 ? String(kdList.length) : undefined,
      status: kdList.length > 0 ? 'done' : 'empty',
    },
    '/app/vady': {
      meta: openVady > 0 ? `${openVady} otevřených` : 'Vše uzavřeno',
      status: openVady > 0 ? 'pending' : 'done',
      count: openVady > 0 ? String(openVady) : undefined,
      highlight: (vadyStats?.po_terminu ?? 0) > 0,
    },
    '/app/rfi': {
      meta: openRfi > 0 ? `${openRfi} čeká na odpověď` : 'Nic neotevřeno',
      status: openRfi > 0 ? 'pending' : 'done',
      count: openRfi > 0 ? String(openRfi) : undefined,
    },
    '/app/harmonogram': {
      meta: harmonogramy.length > 0 ? `${harmonogramy[0].task_count} úkolů` : 'Nezaložen',
      status: harmonogramy.length > 0 ? 'done' : 'empty',
    },
    '/app/fotodokumentace': {
      meta: photos.length > 0 ? `${photos.length} fotek` : 'Zatím žádné',
      count: photos.length > 0 ? String(photos.length) : undefined,
      status: photos.length > 0 ? 'done' : 'empty',
    },
    '/app/zmeny': {
      meta: navrhZmen > 0
        ? `${navrhZmen} návrhů · ${schvalenoZmen} schváleno`
        : schvalenoZmen > 0
          ? `${schvalenoZmen} schválených`
          : 'Žádné',
      count: navrhZmen > 0 ? String(navrhZmen) : undefined,
      status: navrhZmen > 0 ? 'pending' : schvalenoZmen > 0 ? 'done' : 'empty',
    },
    '/app/faktura': {
      meta: fakturyDocs.length > 0 ? `${fakturyDocs.length} faktur` : 'Zatím žádné',
      count: fakturyDocs.length > 0 ? String(fakturyDocs.length) : undefined,
      status: fakturyDocs.length > 0 ? 'done' : 'empty',
    },
    '/app/nabidka': {
      meta: nabidkaDocs.length > 0 ? `${nabidkaDocs.length} uložených` : 'Zatím žádná',
      status: nabidkaDocs.length > 0 ? 'done' : 'empty',
    },
  };

  const modules: ModuleCardProps[] = navSections.flatMap((section) =>
    section.items
      .filter((item) => !skip.has(item.to))
      .map((item) => {
        const live = liveData[item.to];
        return {
          to: item.to,
          cat: section.label,
          name: item.label,
          meta: live?.meta ?? staticMeta[item.to] ?? '',
          status: live?.status ?? 'info',
          count: live?.count,
          highlight: live?.highlight,
        };
      }),
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroHead}>
          <div className={styles.heroTitle}>
            <div className={styles.eyebrow}>
              Stavba · {activeStavba.zakazka_cislo || `#${activeStavba.id}`}
            </div>
            <h1 className={styles.titleRow}>{activeStavba.nazev}</h1>
            <div className={styles.subtitle}>{subtitleParts.join(' · ')}</div>
          </div>
          <div className={styles.progress}>
            <div className={styles.progressLabel}>Postup</div>
            <div className={styles.progressValue}>— %</div>
          </div>
        </div>

        <ProgressBar value={0} thickness="md" />

        <div className={styles.alerts}>
          {openVady > 0 && (
            <Badge tone="danger" pill dot>
              {openVady} otevřených vad
            </Badge>
          )}
          {vadyStats && vadyStats.po_terminu > 0 && (
            <Badge tone="yellow" pill dot>
              {vadyStats.po_terminu} vad po termínu
            </Badge>
          )}
          {openRfi > 0 && (
            <Badge tone="yellow" pill dot>
              {openRfi} otevřených dotazů k projektu
            </Badge>
          )}
          {!dnesDenik && (
            <Badge tone="yellow" pill dot>
              Dnes chybí zápis do deníku
            </Badge>
          )}
          {openVady === 0 && openRfi === 0 && dnesDenik && (
            <Badge tone="success" pill dot>
              Vše v pořádku
            </Badge>
          )}
        </div>
      </section>

      {/* Modules grid */}
      <section>
        <div className={styles.sectionLabel}>Moduly stavby</div>
        <div className={styles.modulesGrid}>
          {modules.map((m) => (
            <ModuleCard key={m.name} {...m} />
          ))}
        </div>
      </section>

      {/* Poslední dokumenty */}
      <section>
        <div className={styles.sectionLabel}>Poslední dokumenty</div>
        <div className={styles.list}>
          {documents.length === 0 ? (
            <div style={{ color: 'var(--sd-text-dim)', padding: 12 }}>
              Zatím žádné uložené dokumenty.
            </div>
          ) : (
            documents.slice(0, 8).map((d) => (
              <div key={d.id} className={styles.entry}>
                <div className={styles.entryTime}>{formatCzDate(d.updated_at || d.created_at)}</div>
                <div className={cx(styles.entryDot, styles.info)} />
                <div className={styles.entryText}>
                  <b>{d.doc_type}</b> <span>{d.title}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Účastníci: placeholder pro budoucí endpoint */}
      <section>
        <div className={styles.sectionLabel}>Účastníci stavby</div>
        <div className={styles.list}>
          <div className={styles.person}>
            <Avatar initials="JN" size="sm" current />
            <div className={styles.personInfo}>
              <div className={styles.personName}>Stavbyvedoucí (vy)</div>
              <div className={styles.personRole}>
                Účastníky doplníte přes sekci <Link to="/app/subdodavatele">Kontakty</Link>.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ModuleCard({ to, cat, name, meta, status, count, highlight }: ModuleCardProps) {
  const statusLabel =
    status === 'done' ? '✓' :
    status === 'pending' ? '!' :
    status === 'draft' ? '○' :
    status === 'empty' ? '+' :
    status === 'future' ? '◷' :
    '·';

  const statusClass =
    status === 'done' ? styles.statusDone :
    status === 'pending' ? styles.statusPending :
    status === 'draft' ? styles.statusDraft :
    status === 'empty' ? styles.statusEmpty :
    status === 'future' ? styles.statusFuture :
    styles.statusEmpty;

  return (
    <Link to={to} className={cx(styles.module, highlight && styles.moduleHighlight)}>
      <div className={styles.moduleHead}>
        <div className={styles.moduleCat}>{cat}</div>
        {count && <div className={styles.moduleCount}>{count}</div>}
      </div>
      <div className={styles.moduleName}>{name}</div>
      <div className={styles.moduleFoot}>
        <div className={cx(styles.statusBox, statusClass)}>{statusLabel}</div>
        <div className={cx(styles.moduleMeta, (status === 'pending' || status === 'draft') && styles.pendingText)}>
          {meta}
        </div>
      </div>
    </Link>
  );
}
